import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const NotificationTestUpdated = () => {
  const { session, userProfile } = useAuth();
  const [title, setTitle] = useState('Test Notification');
  const [message, setMessage] = useState('This is a test web push notification!');
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSendTestNotification = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const token = session?.access_token;
      if (!token) {
        setError('No authentication token available. Please log in.');
        setLoading(false);
        return;
      }

      // Use the new merchant notification endpoint
      const endpoint = userProfile?.role === 'merchant' 
        ? '/api/test/merchant/send-notification'
        : '/api/test/admin/send-notification';

      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'system_alert',
          title: title,
          message: message,
          metadata: {
            test: true,
            timestamp: new Date().toISOString(),
            source: 'admin-panel'
          }
        })
      });

      const data = await res.json();
      if (res.ok) {
        setResponse(data);
      } else {
        setError(data.error || 'Failed to send test notification');
      }
    } catch (err) {
      console.error('Error sending test notification:', err);
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleTestPaymentNotification = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const token = session?.access_token;
      if (!token) {
        setError('No authentication token available. Please log in.');
        setLoading(false);
        return;
      }

      // Test payment notification for merchants
      if (userProfile?.role !== 'merchant') {
        setError('Payment notifications are only available for merchants');
        setLoading(false);
        return;
      }

      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/test/merchant/payment-received`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: 150,
          customerName: 'John Doe',
          paymentMethod: 'QR Code',
          metadata: {
            test: true,
            timestamp: new Date().toISOString()
          }
        })
      });

      const data = await res.json();
      if (res.ok) {
        setResponse(data);
      } else {
        setError(data.error || 'Failed to send payment notification');
      }
    } catch (err) {
      console.error('Error sending payment notification:', err);
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 border rounded-lg shadow-sm dark:bg-gray-800 dark:border-gray-700">
      <h3 className="text-lg font-semibold mb-4 dark:text-gray-100">
        Test Notifications (Updated API)
      </h3>
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows="3"
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          ></textarea>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSendTestNotification}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send Test Notification'}
          </button>
          {userProfile?.role === 'merchant' && (
            <button
              onClick={handleTestPaymentNotification}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Test Payment Notification'}
            </button>
          )}
        </div>
      </div>
      {response && (
        <div className="mt-4 p-3 bg-green-100 text-green-800 rounded-md dark:bg-green-900 dark:text-green-200">
          <p className="font-semibold">Success:</p>
          <pre className="whitespace-pre-wrap text-sm">{JSON.stringify(response, null, 2)}</pre>
        </div>
      )}
      {error && (
        <div className="mt-4 p-3 bg-red-100 text-red-800 rounded-md dark:bg-red-900 dark:text-red-200">
          <p className="font-semibold">Error:</p>
          <pre className="whitespace-pre-wrap text-sm">{error}</pre>
        </div>
      )}
    </div>
  );
};

export default NotificationTestUpdated;
